import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Button,
    Badge,
    Card,
    CardBody,
    InputGroup,
    InputGroupText,
    Input,
    Alert,
    Spinner
} from 'reactstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { FacebookShareButton, TwitterShareButton, LinkedinShareButton } from 'react-share';
import { FaFacebookF, FaTwitter, FaLinkedinIn, FaBookmark, FaCalendarAlt, FaUser, FaClock, FaEye, FaChevronLeft, FaShareAlt } from 'react-icons/fa';
import { createClient } from '@supabase/supabase-js';
import './BlogPostPage.scss';

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

const BlogPostPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [showSidebar, setShowSidebar] = useState(false);
    const [post, setPost] = useState(null);
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [sidebarItems, setSidebarItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingComments, setLoadingComments] = useState(false);
    const [comment, setComment] = useState('');
    const [comments, setComments] = useState([]);
    const [bookmarked, setBookmarked] = useState(false);
    const [error, setError] = useState(null);

    // Fetch blog post data from Supabase
    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchBlogPostData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch the current post
                const { data: postData, error: postError } = await supabase
                    .from('blog_posts')
                    .select(`
                        *,
                        author:blog_authors(*),
                        category:blog_categories(*),
                        tags:blog_post_tags(
                            tag:blog_tags(*)
                        )
                    `)
                    .eq('slug', slug)
                    .eq('is_published', true)
                    .single();

                if (postError) {
                    if (postError.code === 'PGRST116') {
                        // Post not found
                        navigate('/blog');
                        return;
                    }
                    throw postError;
                }

                // Increment view count
                await supabase.rpc('increment_post_view', { p_post_slug: slug });

                // Format post data
                const formattedPost = {
                    id: postData.id,
                    title: postData.title,
                    slug: postData.slug,
                    excerpt: postData.excerpt,
                    content: postData.content,
                    featured_image: postData.featured_image,
                    author_name: postData.author?.name,
                    author_slug: postData.author?.slug,
                    author_title: postData.author?.title,
                    author_bio: postData.author?.bio,
                    author_avatar: postData.author?.avatar_url,
                    author_social: postData.author?.social_links,
                    category_name: postData.category?.name,
                    category_slug: postData.category?.slug,
                    tags: (postData.tags || []).map(t => t.tag).filter(t => t),
                    is_featured: postData.is_featured,
                    read_time_minutes: postData.read_time_minutes,
                    view_count: postData.view_count + 1, // Incremented locally
                    share_count: postData.share_count,
                    published_at: postData.published_at,
                    created_at: postData.created_at,
                };

                setPost(formattedPost);

                // Fetch related posts (based on tags)
                if (postData.tags?.length > 0) {
                    const tagIds = postData.tags.map(t => t.tag?.id).filter(id => id);

                    if (tagIds.length > 0) {
                        const { data: relatedData, error: relatedError } = await supabase
                            .from('blog_posts')
                            .select(`
                                *,
                                author:blog_authors(*),
                                category:blog_categories(*)
                            `)
                            .eq('is_published', true)
                            .neq('id', postData.id)
                            .in('blog_post_tags.tag_id', tagIds)
                            .order('published_at', { ascending: false })
                            .limit(3);

                        if (!relatedError) {
                            const formattedRelated = relatedData.map(post => ({
                                id: post.id,
                                title: post.title,
                                slug: post.slug,
                                excerpt: post.excerpt,
                                featured_image: post.featured_image,
                                author_name: post.author?.name,
                                category_name: post.category?.name,
                                read_time_minutes: post.read_time_minutes,
                                published_at: post.published_at,
                            }));
                            setRelatedPosts(formattedRelated);
                        }
                    }
                }

                // Fetch categories for sidebar
                const { data: categories, error: categoriesError } = await supabase
                    .from('blog_categories')
                    .select(`
                        *,
                        blog_posts(count)
                    `)
                    .eq('is_active', true)
                    .order('sort_order');

                if (!categoriesError) {
                    const formattedCategories = categories.map(cat => ({
                        id: cat.id,
                        name: cat.name,
                        slug: cat.slug,
                        description: cat.description,
                        icon: cat.icon,
                        count: cat.blog_posts?.[0]?.count || 0,
                    }));
                    setSidebarItems(formattedCategories);
                }

                // Fetch approved comments
                await fetchComments(postData.id);

            } catch (err) {
                console.error('Error fetching blog post:', err);
                setError('Failed to load the blog post. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchBlogPostData();
        }
    }, [slug, navigate]);

    const fetchComments = async (postId) => {
        try {
            setLoadingComments(true);
            const { data: commentsData, error: commentsError } = await supabase
                .from('blog_comments')
                .select('*')
                .eq('post_id', postId)
                .eq('is_approved', true)
                .order('created_at', { ascending: false });

            if (!commentsError) {
                setComments(commentsData || []);
            }
        } catch (err) {
            console.error('Error fetching comments:', err);
        } finally {
            setLoadingComments(false);
        }
    };

    const toggleSidebar = () => {
        setShowSidebar(!showSidebar);
    };

    const handleBookmark = () => {
        setBookmarked(!bookmarked);
        // In a real app, you would save to user's bookmarks in Supabase
    };

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        if (!comment.trim() || !post) return;

        try {
            // Get current user info (if authenticated)
            const { data: { user } } = await supabase.auth.getUser();

            const newComment = {
                post_id: post.id,
                author_name: user?.user_metadata?.full_name || 'Anonymous',
                author_email: user?.email || '',
                content: comment.trim(),
                user_id: user?.id || null,
                is_approved: !user, // Auto-approve for authenticated users
                created_at: new Date().toISOString(),
            };

            const { error: insertError } = await supabase
                .from('blog_comments')
                .insert([newComment]);

            if (insertError) throw insertError;

            // Refresh comments
            await fetchComments(post.id);
            setComment('');

            // Show success message
            alert('Comment submitted successfully!');

        } catch (err) {
            console.error('Error submitting comment:', err);
            alert('Failed to submit comment. Please try again.');
        }
    };

    const handleShare = async (platform) => {
        try {
            // Increment share count in database
            await supabase
                .from('blog_posts')
                .update({ share_count: (post.share_count || 0) + 1 })
                .eq('id', post.id);

            // Update local state
            setPost(prev => ({
                ...prev,
                share_count: (prev.share_count || 0) + 1
            }));
        } catch (err) {
            console.error('Error updating share count:', err);
        }
    };

    const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
    const title = post?.title || '';

    if (loading) {
        return (
            <div className="blog-post-loading">
                <Container>
                    <div className="text-center py-5">
                        <Spinner color="primary" size="lg" />
                        <p className="mt-3">Loading article...</p>
                    </div>
                </Container>
            </div>
        );
    }

    if (error || !post) {
        return (
            <Container className="py-5">
                <Alert color="warning">
                    <h4>Article not found</h4>
                    <p>{error || 'The blog post you\'re looking for doesn\'t exist or has been moved.'}</p>
                    <Button color="primary" tag={Link} to="/blog">
                        Back to Blog
                    </Button>
                </Alert>
            </Container>
        );
    }

    return (
        <div className="blog-post-page">
            {/* Mobile Menu Button */}
            <div className="mobile-menu-button d-lg-none">
                <Button
                    color="primary"
                    onClick={toggleSidebar}
                    className="sidebar-toggle-btn"
                >
                    <i className="fas fa-filter mr-2"></i>
                    More Articles
                </Button>
            </div>

            <Container>
                {/* Back Button */}
                <div className="back-to-blog mb-4">
                    <Button
                        color="link"
                        tag={Link}
                        to="/blog"
                        className="back-btn"
                    >
                        <FaChevronLeft className="me-2" />
                        Back to Blog
                    </Button>
                </div>

                <Row className="blog-post-container">
                    {/* Sidebar Column */}
                    <Col lg="3" xl="3" className="d-none d-lg-block">
                        <div className="sidebar-column">
                            <div className="sidebar-wrapper">
                                <div className="sidebar-header">
                                    <h3 className="mb-3">
                                        <i className="fas fa-filter mr-2"></i>
                                        Categories
                                    </h3>
                                    <p className="text-muted mb-4">
                                        Browse more articles
                                    </p>
                                </div>

                                <div className="sidebar-content">
                                    {/* All Posts Link */}
                                    <Link
                                        to="/blog"
                                        className="category-item"
                                    >
                                        <i className="fas fa-newspaper category-icon"></i>
                                        <span className="category-name">All Articles</span>
                                    </Link>

                                    {/* Dynamic Categories */}
                                    {sidebarItems.map((categoryItem) => (
                                        <div key={categoryItem.id} className="sidebar-category">
                                            <Link
                                                to={`/blog/category/${categoryItem.slug}`}
                                                className={`category-item ${post.category_slug === categoryItem.slug ? 'active' : ''}`}
                                            >
                                                <i className={`${categoryItem.icon || 'fas fa-folder'} category-icon`}></i>
                                                <span className="category-name">{categoryItem.name}</span>
                                                {categoryItem.count > 0 && (
                                                    <Badge color="light" pill className="ms-2">
                                                        {categoryItem.count}
                                                    </Badge>
                                                )}
                                            </Link>
                                        </div>
                                    ))}
                                </div>

                                <div className="sidebar-footer">
                                    {relatedPosts.length > 0 && (
                                        <div className="mb-4">
                                            <h5 className="mb-3">Related Articles</h5>
                                            <div className="popular-posts">
                                                {relatedPosts.slice(0, 3).map((related) => (
                                                    <Link
                                                        key={related.id}
                                                        to={`/blog/${related.slug}`}
                                                        className="popular-post-item"
                                                    >
                                                        <div className="popular-post-content">
                                                            <h6 className="popular-post-title">{related.title}</h6>
                                                            <div className="popular-post-meta">
                                                                <span>{format(new Date(related.published_at), 'MMM d')}</span>
                                                                <span>• {related.read_time_minutes} min read</span>
                                                            </div>
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <Button
                                        tag={Link}
                                        to="/blog/subscribe"
                                        color="primary"
                                        outline
                                        block
                                    >
                                        <i className="fas fa-envelope mr-2"></i>
                                        Get Updates
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Col>

                    {/* Mobile Sidebar */}
                    <Col className={`sidebar-column d-lg-none ${showSidebar ? 'show' : ''}`}>
                        <div className="sidebar-wrapper">
                            <div className="sidebar-header">
                                <h3 className="mb-3">
                                    <i className="fas fa-filter mr-2"></i>
                                    Categories
                                </h3>
                                <button
                                    className="sidebar-close-btn"
                                    onClick={toggleSidebar}
                                    aria-label="Close sidebar"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>

                            <div className="sidebar-content">
                                <Link
                                    to="/blog"
                                    className="category-item"
                                    onClick={() => setShowSidebar(false)}
                                >
                                    <i className="fas fa-newspaper category-icon"></i>
                                    <span className="category-name">All Articles</span>
                                </Link>

                                {sidebarItems.map((categoryItem) => (
                                    <div key={categoryItem.id} className="sidebar-category">
                                        <Link
                                            to={`/blog/category/${categoryItem.slug}`}
                                            className="category-item"
                                            onClick={() => setShowSidebar(false)}
                                        >
                                            <i className={`${categoryItem.icon || 'fas fa-folder'} category-icon`}></i>
                                            <span className="category-name">{categoryItem.name}</span>
                                        </Link>
                                    </div>
                                ))}
                            </div>

                            <div className="sidebar-footer">
                                <Button
                                    tag={Link}
                                    to="/blog/subscribe"
                                    color="primary"
                                    outline
                                    block
                                    onClick={() => setShowSidebar(false)}
                                >
                                    <i className="fas fa-envelope mr-2"></i>
                                    Get Updates
                                </Button>
                            </div>
                        </div>
                        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
                    </Col>

                    {/* Main Content Column */}
                    <Col lg="9" xl="9">
                        <div className="main-content-column">
                            {/* Article Header */}
                            <div className="article-header mb-5">
                                <div className="article-category mb-3">
                                    {post.category_name && (
                                        <Badge color="primary" className="category-badge">
                                            {post.category_name}
                                        </Badge>
                                    )}
                                </div>

                                <h1 className="article-title mb-4">{post.title}</h1>

                                <div className="article-meta mb-4">
                                    <div className="d-flex flex-wrap align-items-center gap-3">
                                        <div className="author-info d-flex align-items-center">
                                            <img
                                                src={post.author_avatar || 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'}
                                                alt={post.author_name}
                                                className="author-avatar me-3"
                                                onError={(e) => {
                                                    e.target.src = 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80';
                                                }}
                                            />
                                            <div>
                                                <h6 className="mb-0">{post.author_name}</h6>
                                                <small className="text-muted">{post.author_title}</small>
                                            </div>
                                        </div>
                                        <div className="meta-items d-flex flex-wrap gap-3">
                                            <span className="meta-item">
                                                <FaCalendarAlt className="me-2" />
                                                {post.published_at ? format(new Date(post.published_at), 'MMMM d, yyyy') : 'Recently'}
                                            </span>
                                            <span className="meta-item">
                                                <FaClock className="me-2" />
                                                {post.read_time_minutes || 5} min read
                                            </span>
                                            <span className="meta-item">
                                                <FaEye className="me-2" />
                                                {(post.view_count || 0).toLocaleString()} views
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Article Actions */}
                                <div className="article-actions d-flex justify-content-between align-items-center mb-4">
                                    <div className="social-share">
                                        <span className="me-3">Share:</span>
                                        <FacebookShareButton
                                            url={shareUrl}
                                            quote={title}
                                            onClick={() => handleShare('facebook')}
                                        >
                                            <Button color="light" size="sm" className="me-2">
                                                <FaFacebookF />
                                            </Button>
                                        </FacebookShareButton>
                                        <TwitterShareButton
                                            url={shareUrl}
                                            title={title}
                                            onClick={() => handleShare('twitter')}
                                        >
                                            <Button color="light" size="sm" className="me-2">
                                                <FaTwitter />
                                            </Button>
                                        </TwitterShareButton>
                                        <LinkedinShareButton
                                            url={shareUrl}
                                            title={title}
                                            onClick={() => handleShare('linkedin')}
                                        >
                                            <Button color="light" size="sm" className="me-2">
                                                <FaLinkedinIn />
                                            </Button>
                                        </LinkedinShareButton>
                                        <Button
                                            color="light"
                                            size="sm"
                                            onClick={() => {
                                                navigator.clipboard.writeText(shareUrl);
                                                alert('Link copied to clipboard!');
                                                handleShare('copy');
                                            }}
                                        >
                                            <FaShareAlt />
                                        </Button>
                                    </div>
                                    <Button
                                        color="light"
                                        onClick={handleBookmark}
                                        className={bookmarked ? 'bookmarked' : ''}
                                    >
                                        <FaBookmark className={bookmarked ? 'text-primary' : ''} />
                                        <span className="ms-2">{bookmarked ? 'Saved' : 'Save'}</span>
                                    </Button>
                                </div>

                                {/* Featured Image */}
                                <div className="article-featured-image mb-5">
                                    <img
                                        src={post.featured_image || 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'}
                                        alt={post.title}
                                        className="img-fluid rounded"
                                        onError={(e) => {
                                            e.target.src = 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80';
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Article Content */}
                            <div className="article-content mb-5">
                                <div className="content-body" dangerouslySetInnerHTML={{ __html: post.content }} />

                                {/* Article Tags */}
                                <div className="article-tags mt-5">
                                    <h6 className="mb-3">Tags:</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        {post.tags?.map((tag, index) => (
                                            <Badge
                                                key={index}
                                                color="light"
                                                className="tag-badge"
                                                tag={Link}
                                                to={`/blog/tag/${tag.slug}`}
                                            >
                                                {tag.name}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Author Bio */}
                            <div className="author-bio card mb-5">
                                <CardBody>
                                    <Row className="align-items-center">
                                        <Col md="auto" className="mb-3 mb-md-0">
                                            <img
                                                src={post.author_avatar || 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'}
                                                alt={post.author_name}
                                                className="author-bio-avatar"
                                                onError={(e) => {
                                                    e.target.src = 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80';
                                                }}
                                            />
                                        </Col>
                                        <Col>
                                            <h5 className="mb-2">About the Author</h5>
                                            <h6 className="text-primary mb-2">{post.author_name}</h6>
                                            <p className="mb-0 text-muted">{post.author_bio}</p>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </div>

                            {/* Comments Section */}
                            <div className="comments-section mb-5">
                                <h4 className="mb-4">
                                    Comments ({comments.length})
                                </h4>

                                {/* Comment Form */}
                                <Card className="mb-4">
                                    <CardBody>
                                        <h6 className="mb-3">Leave a Comment</h6>
                                        <form onSubmit={handleCommentSubmit}>
                                            <div className="mb-3">
                                                <Input
                                                    type="textarea"
                                                    placeholder="Share your thoughts..."
                                                    rows="3"
                                                    value={comment}
                                                    onChange={(e) => setComment(e.target.value)}
                                                    disabled={loadingComments}
                                                />
                                            </div>
                                            <Button type="submit" color="primary" disabled={loadingComments || !comment.trim()}>
                                                {loadingComments ? 'Posting...' : 'Post Comment'}
                                            </Button>
                                        </form>
                                    </CardBody>
                                </Card>

                                {/* Comments List */}
                                {loadingComments ? (
                                    <div className="text-center py-3">
                                        <Spinner color="primary" size="sm" />
                                        <p className="mt-2">Loading comments...</p>
                                    </div>
                                ) : comments.length > 0 ? (
                                    <div className="comments-list">
                                        {comments.map((commentItem) => (
                                            <Card key={commentItem.id} className="mb-3">
                                                <CardBody>
                                                    <div className="d-flex">
                                                        <div className="comment-avatar-placeholder me-3">
                                                            <FaUser className="text-muted" size={40} />
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <div>
                                                                    <h6 className="mb-0">{commentItem.author_name}</h6>
                                                                    {commentItem.author_email && (
                                                                        <small className="text-muted">{commentItem.author_email}</small>
                                                                    )}
                                                                </div>
                                                                <small className="text-muted">
                                                                    {format(new Date(commentItem.created_at), 'MMM d, yyyy')}
                                                                </small>
                                                            </div>
                                                            <p className="mb-0">{commentItem.content}</p>
                                                        </div>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <p className="text-muted">No comments yet. Be the first to comment!</p>
                                    </div>
                                )}
                            </div>

                            {/* Related Articles */}
                            {relatedPosts.length > 0 && (
                                <div className="related-articles mb-5">
                                    <h4 className="mb-4">Related Articles</h4>
                                    <Row>
                                        {relatedPosts.map((related) => (
                                            <Col md="6" lg="4" key={related.id} className="mb-4">
                                                <Card className="related-post-card h-100">
                                                    <div className="post-image-wrapper">
                                                        <img
                                                            src={related.featured_image || 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'}
                                                            alt={related.title}
                                                            className="post-image"
                                                            onError={(e) => {
                                                                e.target.src = 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
                                                            }}
                                                        />
                                                    </div>
                                                    <CardBody>
                                                        <div className="post-meta mb-2">
                                                            {related.category_name && (
                                                                <Badge color="light" className="me-2">
                                                                    {related.category_name}
                                                                </Badge>
                                                            )}
                                                            <small className="text-muted">
                                                                {format(new Date(related.published_at), 'MMM d')}
                                                            </small>
                                                        </div>
                                                        <h6 className="post-title mb-2">
                                                            {related.title}
                                                        </h6>
                                                        <p className="post-excerpt small text-muted mb-3">
                                                            {related.excerpt}
                                                        </p>
                                                        <Link
                                                            to={`/blog/${related.slug}`}
                                                            className="read-more-link"
                                                        >
                                                            Read More <i className="fas fa-arrow-right ms-1"></i>
                                                        </Link>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            )}

                            {/* Newsletter CTA */}
                            <div className="newsletter-cta card mb-5">
                                <CardBody className="text-center">
                                    <h4 className="mb-3">Want More Insights Like This?</h4>
                                    <p className="text-muted mb-4">
                                        Subscribe to our newsletter and get the latest digital marketing strategies delivered to your inbox.
                                    </p>
                                    <InputGroup className="mb-3" style={{ maxWidth: '400px', margin: '0 auto' }}>
                                        <Input type="email" placeholder="Your email address" />
                                        <Button color="primary">
                                            Subscribe
                                        </Button>
                                    </InputGroup>
                                    <small className="text-muted">
                                        No spam. Unsubscribe anytime.
                                    </small>
                                </CardBody>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default BlogPostPage;